import type {Crumb} from "./use-breadcrumbs";
import type {Key, MouseEvent} from "react";

import {
  Button,
  Dropdown,
  Breadcrumbs as HeroUIBreadcrumbs,
  Label,
} from "@project/design-system-web";
import {useI18n} from "@project/i18n/react";
import {useNavigate} from "@tanstack/react-router";
import {useCallback, useLayoutEffect, useMemo, useRef, useState} from "react";

import {appShellMessages} from "../../../messages/app-shell.messages";

import {useBreadcrumbs} from "./use-breadcrumbs";

type BreadcrumbsPlacement = "desktop" | "mobile";

type BreadcrumbSegment =
  {type: "crumb"; crumb: Crumb} | {type: "ellipsis"; id: string; hiddenCrumbs: Crumb[]};

interface BreadcrumbsProps {
  placement?: BreadcrumbsPlacement;
}

/**
 * Breadcrumbs — adaptive breadcrumb trail derived from `useBreadcrumbs()`.
 *
 * The trail preserves route semantics (`[module] > page > subpage > current`) while
 * adapting to the actual available header width. Middle ancestors collapse into an
 * interactive ellipsis menu first; visible labels truncate individually and expose
 * full text through `title`/menu labels.
 */
export function Breadcrumbs({placement = "desktop"}: BreadcrumbsProps) {
  const allCrumbs = useBreadcrumbs();
  const navigate = useNavigate();
  const containerRef = useRef<HTMLDivElement>(null);
  const measureRefs = useRef<Array<HTMLDivElement | null>>([]);
  const frameRef = useRef<number | null>(null);
  const crumbSignature = allCrumbs
    .map((crumb) => `${crumb.id}:${crumb.label}:${crumb.pathname}:${crumb.isModule === true}`)
    .join("|");
  const candidates = useMemo(
    () => buildBreadcrumbCandidates(allCrumbs, placement),
    [crumbSignature, placement],
  );
  const [candidateIndex, setCandidateIndex] = useState(0);
  const [openEllipsisId, setOpenEllipsisId] = useState<string | null>(null);

  const selectedCandidate =
    candidates[Math.min(candidateIndex, Math.max(0, candidates.length - 1))];

  const measure = useCallback(() => {
    if (frameRef.current !== null) cancelAnimationFrame(frameRef.current);

    frameRef.current = requestAnimationFrame(() => {
      frameRef.current = null;

      const containerWidth = containerRef.current?.clientWidth ?? 0;

      if (containerWidth <= 0 || candidates.length === 0) return;

      const nextIndex = candidates.findIndex((_, index) => {
        const node = measureRefs.current[index];
        const width = node?.getBoundingClientRect().width ?? node?.scrollWidth;

        return width !== undefined && width <= containerWidth;
      });
      const selectedIndex = nextIndex === -1 ? candidates.length - 1 : nextIndex;

      setCandidateIndex((currentIndex) =>
        currentIndex === selectedIndex ? currentIndex : selectedIndex,
      );
    });
  }, [candidates]);

  useLayoutEffect(() => {
    setCandidateIndex((currentIndex) => Math.min(currentIndex, Math.max(0, candidates.length - 1)));
    measure();

    const container = containerRef.current;

    if (!container) return;

    const observer = new ResizeObserver(measure);

    observer.observe(container);

    void document.fonts?.ready.then(measure);

    return () => {
      observer.disconnect();
      if (frameRef.current !== null) cancelAnimationFrame(frameRef.current);
    };
  }, [candidates, measure, placement]);

  if (allCrumbs.length === 0 || !selectedCandidate) return null;

  const hiddenCrumbsById = new Map(
    selectedCandidate
      .filter(
        (segment): segment is Extract<BreadcrumbSegment, {type: "ellipsis"}> =>
          segment.type === "ellipsis",
      )
      .map((segment) => [segment.id, segment.hiddenCrumbs]),
  );

  function handleCrumbNavigate(crumb: Crumb, event: MouseEvent) {
    event.preventDefault();
    void navigate({to: crumb.pathname});
  }

  function handleHiddenCrumbAction(key: Key) {
    for (const hiddenCrumbs of hiddenCrumbsById.values()) {
      const crumb = hiddenCrumbs.find((item) => item.id === key);

      if (crumb && !crumb.isModule) void navigate({to: crumb.pathname});
    }
  }

  return (
    <div ref={containerRef} className="relative max-w-full min-w-0 overflow-hidden">
      <HeroUIBreadcrumbs className={breadcrumbsClassName}>
        {selectedCandidate.map((segment, index) =>
          renderSegment(segment, {
            isLast: index === selectedCandidate.length - 1,
            onCrumbNavigate: handleCrumbNavigate,
            onHiddenCrumbAction: handleHiddenCrumbAction,
            onEllipsisOpenChange: setOpenEllipsisId,
            openEllipsisId,
            placement,
            visibleSegmentCount: selectedCandidate.length,
          }),
        )}
      </HeroUIBreadcrumbs>

      <div aria-hidden="true" className="pointer-events-none absolute top-0 left-0 -z-10 opacity-0">
        {candidates.map((candidate, index) => (
          <div
            className="inline-block w-max max-w-none"
            key={candidate.map(getSegmentKey).join("|")}
            ref={(node) => {
              measureRefs.current[index] = node;
            }}
          >
            <HeroUIBreadcrumbs className={measurementBreadcrumbsClassName}>
              {candidate.map((segment, segmentIndex) =>
                renderSegment(segment, {
                  isLast: segmentIndex === candidate.length - 1,
                  isMeasuring: true,
                  onCrumbNavigate: handleCrumbNavigate,
                  onHiddenCrumbAction: handleHiddenCrumbAction,
                  onEllipsisOpenChange: setOpenEllipsisId,
                  openEllipsisId,
                  placement,
                  visibleSegmentCount: candidate.length,
                }),
              )}
            </HeroUIBreadcrumbs>
          </div>
        ))}
      </div>
    </div>
  );
}

const breadcrumbsClassName =
  "w-full min-w-0 max-w-full overflow-hidden [&_.breadcrumbs__item]:min-w-0 [&_.breadcrumbs__item]:max-w-full [&_.breadcrumbs__item]:shrink [&_.breadcrumbs__link]:min-w-0 [&_.breadcrumbs__link]:truncate [&_.breadcrumbs__link]:no-underline";

const measurementBreadcrumbsClassName =
  "w-max min-w-max max-w-none overflow-visible [&_.breadcrumbs__item]:min-w-0 [&_.breadcrumbs__item]:max-w-none [&_.breadcrumbs__item]:shrink-0 [&_.breadcrumbs__link]:min-w-0 [&_.breadcrumbs__link]:truncate [&_.breadcrumbs__link]:no-underline";

function buildBreadcrumbCandidates(
  crumbs: Crumb[],
  placement: BreadcrumbsPlacement,
): BreadcrumbSegment[][] {
  if (crumbs.length <= 2) return [toCrumbSegments(crumbs)];

  const candidates =
    placement === "mobile" ? buildMobileCandidates(crumbs) : buildDesktopCandidates(crumbs);

  return dedupeCandidates(candidates.filter((candidate) => candidate.length > 0));
}

function buildDesktopCandidates(crumbs: Crumb[]): BreadcrumbSegment[][] {
  const lastIndex = crumbs.length - 1;
  const parentIndex = crumbs.length - 2;

  return [
    toCrumbSegments(crumbs),
    compact([
      crumbSegment(crumbs[0]),
      ellipsisSegment("middle", crumbs.slice(1, parentIndex)),
      crumbSegment(crumbs[parentIndex]),
      crumbSegment(crumbs[lastIndex]),
    ]),
    compact([
      ellipsisSegment("root-and-middle", crumbs.slice(0, parentIndex)),
      crumbSegment(crumbs[parentIndex]),
      crumbSegment(crumbs[lastIndex]),
    ]),
    compact([
      ellipsisSegment("ancestors", crumbs.slice(0, lastIndex)),
      crumbSegment(crumbs[lastIndex]),
    ]),
  ];
}

function buildMobileCandidates(crumbs: Crumb[]): BreadcrumbSegment[][] {
  const lastIndex = crumbs.length - 1;
  const parentIndex = crumbs.length - 2;

  return [
    toCrumbSegments(crumbs),
    compact([
      ellipsisSegment("ancestors", crumbs.slice(0, parentIndex)),
      crumbSegment(crumbs[parentIndex]),
      crumbSegment(crumbs[lastIndex]),
    ]),
    compact([
      ellipsisSegment("ancestors", crumbs.slice(0, lastIndex)),
      crumbSegment(crumbs[lastIndex]),
    ]),
  ];
}

function toCrumbSegments(crumbs: Crumb[]): BreadcrumbSegment[] {
  return crumbs.map((crumb) => crumbSegment(crumb)).filter((segment) => segment !== null);
}

function crumbSegment(crumb: Crumb | undefined): BreadcrumbSegment | null {
  if (!crumb) return null;

  return {type: "crumb", crumb};
}

function ellipsisSegment(id: string, hiddenCrumbs: Crumb[]): BreadcrumbSegment | null {
  if (hiddenCrumbs.length === 0) return null;

  return {type: "ellipsis", id: `ellipsis-${id}`, hiddenCrumbs};
}

function compact(segments: Array<BreadcrumbSegment | null>): BreadcrumbSegment[] {
  return segments.filter((segment) => segment !== null);
}

function getSegmentKey(segment: BreadcrumbSegment) {
  return segment.type === "crumb"
    ? `crumb:${segment.crumb.id}`
    : `ellipsis:${segment.hiddenCrumbs.map((crumb) => crumb.id).join(",")}`;
}

function dedupeCandidates(candidates: BreadcrumbSegment[][]): BreadcrumbSegment[][] {
  const seen = new Set<string>();

  return candidates.filter((candidate) => {
    const signature = candidate.map(getSegmentKey).join("|");

    if (seen.has(signature)) return false;

    seen.add(signature);

    return true;
  });
}

function renderSegment(
  segment: BreadcrumbSegment,
  options: {
    isLast: boolean;
    isMeasuring?: boolean;
    onCrumbNavigate: (crumb: Crumb, event: MouseEvent) => void;
    onEllipsisOpenChange: (id: string | null) => void;
    onHiddenCrumbAction: (key: Key) => void;
    openEllipsisId: string | null;
    placement: BreadcrumbsPlacement;
    visibleSegmentCount: number;
  },
) {
  if (segment.type === "ellipsis") {
    return renderEllipsisSegment(segment, options);
  }

  return renderCrumbSegment(segment.crumb, options);
}

function renderEllipsisSegment(
  segment: Extract<BreadcrumbSegment, {type: "ellipsis"}>,
  {
    isMeasuring = false,
    onEllipsisOpenChange,
    onHiddenCrumbAction,
    openEllipsisId,
  }: {
    isMeasuring?: boolean;
    onEllipsisOpenChange: (id: string | null) => void;
    onHiddenCrumbAction: (key: Key) => void;
    openEllipsisId: string | null;
  },
) {
  const {t} = useI18n();

  if (isMeasuring) {
    return (
      <HeroUIBreadcrumbs.Item key={segment.id}>
        <span className="block shrink-0 px-1" title={t(appShellMessages.hiddenBreadcrumbs)}>
          …
        </span>
      </HeroUIBreadcrumbs.Item>
    );
  }

  return (
    <HeroUIBreadcrumbs.Item key={segment.id}>
      <Dropdown
        isOpen={openEllipsisId === segment.id}
        onOpenChange={(isOpen) => onEllipsisOpenChange(isOpen ? segment.id : null)}
      >
        <Dropdown.Trigger aria-label={t(appShellMessages.showHiddenBreadcrumbs)}>
          <Button
            aria-label={t(appShellMessages.showHiddenBreadcrumbs)}
            className="h-6 min-w-6 px-1"
            size="sm"
            variant="ghost"
          >
            …
          </Button>
        </Dropdown.Trigger>
        <Dropdown.Popover className="min-w-[220px]" placement="bottom start">
          <Dropdown.Menu onAction={onHiddenCrumbAction}>
            {segment.hiddenCrumbs.map((crumb) => (
              <Dropdown.Item
                id={crumb.id}
                isDisabled={crumb.isModule}
                key={crumb.id}
                textValue={crumb.label}
              >
                <Label className="max-w-72 truncate" title={crumb.label}>
                  {crumb.label}
                </Label>
              </Dropdown.Item>
            ))}
          </Dropdown.Menu>
        </Dropdown.Popover>
      </Dropdown>
    </HeroUIBreadcrumbs.Item>
  );
}

function renderCrumbSegment(
  crumb: Crumb,
  {
    isLast,
    isMeasuring = false,
    onCrumbNavigate,
    placement,
    visibleSegmentCount,
  }: {
    isLast: boolean;
    isMeasuring?: boolean;
    onCrumbNavigate: (crumb: Crumb, event: MouseEvent) => void;
    placement: BreadcrumbsPlacement;
    visibleSegmentCount: number;
  },
) {
  const label = (
    <BreadcrumbLabel
      crumb={crumb}
      isCurrent={isLast}
      placement={placement}
      visibleSegmentCount={visibleSegmentCount}
    />
  );

  if (isLast || isMeasuring) {
    return <HeroUIBreadcrumbs.Item key={crumb.id}>{label}</HeroUIBreadcrumbs.Item>;
  }

  if (crumb.isModule) {
    return (
      <HeroUIBreadcrumbs.Item isDisabled key={crumb.id}>
        {label}
      </HeroUIBreadcrumbs.Item>
    );
  }

  return (
    <HeroUIBreadcrumbs.Item
      href={crumb.pathname}
      key={crumb.id}
      onClick={(event) => onCrumbNavigate(crumb, event)}
    >
      {label}
    </HeroUIBreadcrumbs.Item>
  );
}

function BreadcrumbLabel({
  crumb,
  isCurrent,
  placement,
  visibleSegmentCount,
}: {
  crumb: Crumb;
  isCurrent: boolean;
  placement: BreadcrumbsPlacement;
  visibleSegmentCount: number;
}) {
  const maxWidth = getLabelMaxWidth({crumb, isCurrent, placement, visibleSegmentCount});

  return (
    <span className={`block min-w-0 truncate ${maxWidth}`} title={crumb.label}>
      {crumb.label}
    </span>
  );
}

function getLabelMaxWidth({
  crumb,
  isCurrent,
  placement,
  visibleSegmentCount,
}: {
  crumb: Crumb;
  isCurrent: boolean;
  placement: BreadcrumbsPlacement;
  visibleSegmentCount: number;
}) {
  const isCurrentOnlyWithCollapsedAncestors = isCurrent && visibleSegmentCount <= 2;

  if (placement === "mobile") {
    if (isCurrent) return isCurrentOnlyWithCollapsedAncestors ? "max-w-[65vw]" : "max-w-[55vw]";

    return "max-w-[45vw]";
  }

  if (isCurrent) {
    return isCurrentOnlyWithCollapsedAncestors
      ? "max-w-[min(40rem,50vw)]"
      : "max-w-[min(36rem,45vw)]";
  }

  if (crumb.isModule) return "max-w-[14rem]";

  return "max-w-[24rem]";
}
